import { createRoot as reactCreateRoot, Root } from 'react-dom/client';
import React from 'react';
import { AppContext, AppContextProvider } from '../components/app-context/app-context.tsx';
import { globalAppContext } from '../components/app-context/global-app-context.tsx';

export type ReactRootNode<T extends Element | DocumentFragment = Element | DocumentFragment> = T & {
  /**
   * We need access to a DOM element's react root when we want to unmount it or merge-render into it.
   * So, we save the react root object directly on the DOM elements.
   */
  skReactRoot?: Root;
};

export const isReactRootNode = <T extends Element | DocumentFragment>(
  e: T
): e is ReactRootNode<T> => 'skReactRoot' in e;

/**
 * React containers receive this attribute.
 * It is used to identify react containers afterwards.
 */
export const reactContainerAttr = 'data-react-container';

/**
 * Unmounts all react root components inside the given element and its children.
 * @param parent Pass the parent here, where the component is rendered into.
 *               Don't pass a component itself.
 */
export const unmount = (parent: Element): void => {
  let rootNodesToUnmount: Array<ReactRootNode<Element>> = [];

  // Check the passed element itself.
  if (parent.getAttribute(reactContainerAttr) === 'true' && isReactRootNode(parent)) {
    rootNodesToUnmount.push(parent);
  }

  // Check child elements
  rootNodesToUnmount.push(
    ...Array.from(parent.querySelectorAll(`[${reactContainerAttr}=true]`)).filter(isReactRootNode)
  );

  // Execute unmount
  rootNodesToUnmount.forEach(rootNode => {
    rootNode.skReactRoot?.unmount();
    rootNode.removeAttribute(reactContainerAttr);
    delete rootNode.skReactRoot;
  });
};

/**
 * Wrapper around react's createRoot function, which should be favoured instead of react's native createRoot.
 * We need to be able to identify react roots, so that we can unmount them when needed
 * and thus this function marks those react roots.
 */
export const createRoot: typeof reactCreateRoot = (container, options) => {
  const root = reactCreateRoot(container, options);

  if (container instanceof Element) {
    container.setAttribute(reactContainerAttr, 'true');
  }

  (container as ReactRootNode).skReactRoot = root;

  return root;
};

/**
 * React's createRoot can only be called once on an element.
 * So, in order to merge-render into the same element, it is necessary to differentiate between
 * the first and all subsequent renders. This function does exactly that, as it is only
 * boilerplate for all the places that need it.
 *
 * Also, for convenience, this function wraps the children in an AppContextProvider.
 * This requires that a global appContext exists or is passed as a parameter.
 *
 * Note: React docs state: "In practice, most React apps only call root.render() once.".
 *       However, our code relies on merge-rendering in some places.
 */
export const createRootAndRender = (
  container: Element,
  children: React.ReactNode,
  appContext?: AppContext
) => {
  const appContextOrDefault = appContext || globalAppContext();

  const childrenWithContext = appContextOrDefault ? (
    <AppContextProvider appContext={appContextOrDefault}>{children}</AppContextProvider>
  ) : (
    children
  );

  if (isReactRootNode(container)) {
    container.skReactRoot?.render(childrenWithContext);
  } else {
    createRoot(container).render(childrenWithContext);
  }
};
